import { useState, useEffect } from "react";
import { AlertCircle } from "lucide-react";
import api from "../../../configs/api";
import PropertyCard from "../../../components/public/userCommon/PropertyCard.jsx";
import { TrackImpression } from "../../../components/public/trackers/TrackImpression.jsx";
import ContactOwnerModal from "../../../components/modals/ContactOwnerModal.jsx";
import useContactModal from "../../../hooks/useContactModal.js";
import ContactForm from "../../../components/public/userCommon/ContactForm.jsx";

const ListingProjectProperties = () => {
  // PROJECT PROPERTIES STATE
  const [properties, setProperties] = useState([]);

  // SELECTED PROPERTY FOR MODAL
  const [selectedProperty, setSelectedProperty] = useState(null);

  // LOADING + ERROR
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // MODAL HANDLERS
  const { closeModal, isOpen, openModal } = useContactModal();

  // FETCH PROJECT PROPERTIES
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await api.get("/properties?type=project");

        if (res.data.success !== false && res.data.data) {
          setProperties(res.data.data);
        } else {
          setProperties([]);
        }
      } catch (err) {
        setError(err.message || "Failed to load project properties");
        setProperties([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // LOADING STATE
  if (loading) {
    return (
      <div className="bg-gray-50 py-10">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-600 text-lg">Loading project properties...</p>
        </div>
      </div>
    );
  }

  // ERROR STATE
  if (error) {
    return (
      <div className="bg-gray-50 py-10">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-600 font-semibold mb-2">
              Error Loading Properties
            </p>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // MAIN UI
  return (
    <div className="bg-gray-50">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-10">
          {/* MAIN LISTING SECTION */}
          <div className="lg:col-span-3">
            {/* HEADER */}
            <div className="mb-5 sm:mb-6 md:mb-8">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold mb-2 sm:mb-3 text-gray-900">
                Discover Exclusive Real Estate Projects
              </h2>

              <p className="text-sm sm:text-base text-gray-600">
                {properties.length} Result{properties.length !== 1 ? "s" : ""} |
                Premium Project Listings
              </p>
            </div>

            {/* PROPERTY LIST */}
            <div
              className="
                grid 
                grid-cols-1 
                sm:grid-cols-2
                md:grid-cols-1 
                gap-4 
                sm:gap-5 
                md:gap-6
              "
            >
              {properties.length > 0 ? (
                properties.map((property) => (
                  <TrackImpression key={property._id} propertyId={property._id}>
                    <PropertyCard
                      property={property}
                      openModal={openModal}
                      setPropertyData={setSelectedProperty}
                    />
                  </TrackImpression>
                ))
              ) : (
                <div className="col-span-full bg-white rounded-lg shadow-md p-8 text-center">
                  <div className="flex flex-col items-center justify-center py-8">
                    <AlertCircle className="w-16 h-16 text-gray-400 mb-4" />
                    <p className="text-gray-500 text-lg mb-2">
                      No project properties found
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT SIDEBAR — CONTACT FORM */}
          <div className="lg:col-span-1 sticky top-4 self-start">
            <ContactForm />
          </div>
        </div>
      </div>

      {/* CONTACT MODAL */}
      <ContactOwnerModal
        propertyData={selectedProperty}
        isOpen={isOpen}
        onClose={closeModal}
      />
    </div>
  );
};

export default ListingProjectProperties;
